/** @format */

import {
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  ElementRef,
  OnDestroy,
  PLATFORM_ID,
  Renderer2,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import {
  HomefieldIconLibraryModule,
  HomefieldIconsLibraryService,
  homefieldIconAlertInfoOutline,
  homefieldIconFulfillmentPickupFill,
  homefieldIconFulfillmentSamedayFill,
  homefieldIconSignCancelOutline,
  homefieldIconSignCheckFill,
  homefieldIconFulfillmentShippingFill,
} from '@dsg-tech/homefield-angular-icons';

import { AnalyticsService } from '@shared/data-access/services';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'common-side-nav',
  templateUrl: './side-nav.component.html',
  styleUrl: './side-nav.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, HomefieldIconLibraryModule],
})
export class SideNavComponent implements OnDestroy {
  protected readonly platformId = inject(PLATFORM_ID);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly renderer = inject(Renderer2);
  private readonly document = inject(DOCUMENT);
  private readonly eleRef = inject(ElementRef);
  private readonly homefieldIconsLibraryService = inject(HomefieldIconsLibraryService);

  // Signal inputs
  modalName = input('Default Modal');
  isSidePanelToggled = input<boolean>(undefined);
  panelBodyClass = input('');
  panelContainerClass = input('');
  side = input<'left' | 'right'>('right');
  zipConfirm = input<boolean>();
  addFlexBox = input(false);
  isFormEmpty = input(true);

  // Signal output
  panelEvent = output<{ opened: boolean }>();

  /**
   * @description
   * Whether or not the panel should be displayed
   */
  readonly showContent = signal(false);

  sidePanelRef = viewChild<ElementRef>('sidePanel');

  constructor() {
    this.homefieldIconsLibraryService.registerIcons([
      homefieldIconFulfillmentPickupFill,
      homefieldIconFulfillmentShippingFill,
      homefieldIconAlertInfoOutline,
      homefieldIconSignCheckFill,
      homefieldIconFulfillmentSamedayFill,
      homefieldIconSignCancelOutline,
    ]);

    // Track previous value to only trigger on actual changes, not initial value
    let previousValue: boolean | undefined = undefined;

    // Watch for changes to isSidePanelToggled and handle panel state
    // Only reacts to isSidePanelToggled changes, not sidePanelRef initialization
    effect(() => {
      const isToggled = this.isSidePanelToggled();

      // Use untracked to read sidePanelRef without creating a dependency
      if (untracked(() => this.sidePanelRef()) !== undefined && isToggled !== undefined) {
        // Only trigger panel toggle if value has changed from a previous defined state
        if (previousValue !== undefined && previousValue !== isToggled) {
          if (isToggled) {
            this.panelToggle('open');
          } else {
            this.panelToggle('close');
          }
        }
        previousValue = isToggled;
      }
    });
  }

  ngOnDestroy(): void {
    if (this.showContent()) {
      // Side nav is still open (although it may not be displaying) so we have to
      this.panelToggle('close');
    }
  }

  panelToggle(event: string) {
    if (event === 'open') {
      this.showContent.set(true);

      this.renderer.setStyle(this.document.body, 'overflow', 'hidden');
      this.renderer.addClass(this.document.body, 'side-panel-open');

      this.sendAnalyticsFor(this.modalName());

      this.panelEvent.emit({ opened: true });
      this.eleRef.nativeElement.focus();
    } else {
      /*
       * Closed
       */
      this.showContent.set(false);

      this.renderer.setStyle(this.document.body, 'overflow', 'visible');
      this.renderer.removeClass(this.document.body, 'side-panel-open');

      this.panelEvent.emit({ opened: false });
      this.eleRef.nativeElement.blur();
    }
  }

  sendAnalyticsFor(name: string) {
    /*
     * Analytics Event
     */
    // Set the modal name
    this.analyticsService.setModalName(name);

    // Set the Analytics event type
    this.analyticsService.setType('event', 'ModalOpen');

    // Trigger the Analytics update
    this.analyticsService.triggerAnalyticsEvent('reporting:update');
  }

  onHeaderButtonClick() {
    this.panelToggle('close');
  }

  onHeaderButtonKeydown(event: KeyboardEvent) {
    event.preventDefault();
    if (event.key === 'Enter' || event.key === ' ') {
      this.onHeaderButtonClick();
    }
  }

  onCoverButtonClick() {
    const sidePanel = this.sidePanelRef();
    if (sidePanel?.nativeElement) {
      sidePanel.nativeElement.scrollTop = 0;
    }
    this.panelToggle('open');
  }

  onCoverButtonKeydown(event: KeyboardEvent) {
    event.preventDefault();
    if (event.key === 'Enter' || event.key === ' ') {
      this.onCoverButtonClick();
    }
  }
}
